import { useState, useEffect, useCallback } from 'react';
import { useHotelConfig } from './useHotelConfig';
import { logger } from '../lib/logger';
import { stayService } from '../lib/stay/stayService';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
const STORAGE_KEY = 'push_notification_status';

type NotificationStatus = 'idle' | 'granted' | 'denied' | 'unsupported';

interface UsePushNotificationsReturn {
  status: NotificationStatus;
  isSupported: boolean;
  isSubscribed: boolean;
  subscriberCount: number | null;
  requestPermission: () => Promise<boolean>;
  unsubscribe: () => Promise<void>;
  sendNotification: (params: SendNotificationParams) => Promise<SendResult>;
  loadSubscriberCount: () => Promise<void>;
}

export interface SendNotificationParams {
  title: string;
  body: string;
  url?: string;
  icon?: string;
  sent_by?: string;
}

interface SendResult {
  success: boolean;
  total_sent?: number;
  total_success?: number;
  total_failed?: number;
  error?: string;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function callEdgeFunction(slug: string, method: string, body?: object, queryParams?: string) {
  const url = `${supabaseUrl}/functions/v1/${slug}${queryParams ? `?${queryParams}` : ''}`;
  logger.info(`[EdgeFunction] Calling ${method} ${url}`, { category: 'API', data: { body } });

  const res = await fetch(url, {
    method,
    headers: {
      'Authorization': `Bearer ${supabaseAnonKey}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json();

  if (!res.ok) {
    logger.error(`[EdgeFunction] Error ${res.status}`, { category: 'API', data: { url, status: res.status, response: data } });
    throw new Error(data.error || `Edge function failed with status ${res.status}`);
  }

  logger.info(`[EdgeFunction] Success ${method} ${url}`, { category: 'API', data });
  return data;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const { id: hotelId } = useHotelConfig();

  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasNotification = 'Notification' in window;
  const isSupported = hasServiceWorker && hasPushManager && hasNotification;

  console.log('[PushNotif INIT] Browser support check:');
  console.log('  - serviceWorker:', hasServiceWorker);
  console.log('  - PushManager:', hasPushManager);
  console.log('  - Notification:', hasNotification);
  console.log('  - isSupported:', isSupported);
  console.log('  - HTTPS:', window.location.protocol === 'https:');
  console.log('  - Current permission:', hasNotification ? Notification.permission : 'N/A');

  const [status, setStatus] = useState<NotificationStatus>(() => {
    if (!isSupported) return 'unsupported';
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'granted') return 'granted';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    return 'idle';
  });

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState<number | null>(null);

  const getServiceWorkerRegistration = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return null;
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length > 0) return registrations[0];
    return null;
  }, []);

  useEffect(() => {
    if (!isSupported) return;
    (async () => {
      const reg = await getServiceWorkerRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    })();
  }, [isSupported, getServiceWorkerRegistration]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    console.log('[PushNotif] === FUNCTION START ===');
    console.log('[PushNotif] isSupported:', isSupported);
    console.log('[PushNotif] VAPID_PUBLIC_KEY:', VAPID_PUBLIC_KEY ? 'SET' : 'NOT SET');

    logger.info('[PushNotif] requestPermission called', { category: 'PWA' });

    if (!isSupported) {
      logger.warn('[PushNotif] Push notifications not supported in this browser', { category: 'PWA' });
      console.log('[PushNotif] Returning false: not supported');
      return false;
    }

    if (!VAPID_PUBLIC_KEY) {
      logger.warn('[PushNotif] VITE_VAPID_PUBLIC_KEY not set', { category: 'PWA' });
      console.log('[PushNotif] Returning false: no VAPID key');
      return false;
    }

    logger.info('[PushNotif] Requesting permission...', { category: 'PWA' });
    try {
      const permission = await Notification.requestPermission();
      logger.info('[PushNotif] Permission result:', { category: 'PWA', data: { permission } });

      if (permission !== 'granted') {
        setStatus('denied');
        localStorage.setItem(STORAGE_KEY, 'denied');
        return false;
      }

      logger.info('[PushNotif] Getting service worker registration...', { category: 'PWA' });
      console.log('[PushNotif] About to call getServiceWorkerRegistration...');
      const reg = await getServiceWorkerRegistration();
      console.log('[PushNotif] Registration result:', reg);
      console.log('[PushNotif] Registration active:', reg?.active);
      console.log('[PushNotif] Registration waiting:', reg?.waiting);
      console.log('[PushNotif] Registration installing:', reg?.installing);
      logger.info('[PushNotif] Service worker registration:', { category: 'PWA', data: { found: !!reg } });

      if (!reg) {
        logger.warn('[PushNotif] No service worker registration found', { category: 'PWA' });
        console.error('[PushNotif] ERROR: No service worker registration found');
        return false;
      }

      console.log('[PushNotif] About to subscribe to push manager...');
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      console.log('[PushNotif] Push subscription created:', subscription);

      const key = subscription.getKey('p256dh');
      const authKey = subscription.getKey('auth');
      if (!key || !authKey) return false;

      const p256dh = btoa(String.fromCharCode(...new Uint8Array(key)));
      const auth = btoa(String.fromCharCode(...new Uint8Array(authKey)));

      const stayDates = stayService.getStayDates();
      const subscriptionPayload: Record<string, unknown> = {
        hotel_id: hotelId,
        endpoint: subscription.endpoint,
        p256dh,
        auth,
        user_agent: navigator.userAgent,
        language: navigator.language || 'fr',
      };
      if (stayDates) {
        subscriptionPayload.check_in = stayDates.startDate.split('T')[0];
        subscriptionPayload.check_out = stayDates.endDate.split('T')[0];
      }

      logger.info('[PushNotif] Saving subscription to database', { category: 'PWA', data: subscriptionPayload });
      console.log('[PushNotif] Calling edge function with payload:', subscriptionPayload);
      const saveResult = await callEdgeFunction('save-push-subscription', 'POST', subscriptionPayload);
      console.log('[PushNotif] Edge function response:', saveResult);
      logger.info('[PushNotif] Subscription saved successfully', { category: 'PWA', data: saveResult });

      setStatus('granted');
      setIsSubscribed(true);
      localStorage.setItem(STORAGE_KEY, 'granted');
      return true;
    } catch (err) {
      console.error('[PushNotif] ERROR during subscription:', err);
      console.error('[PushNotif] Error stack:', err instanceof Error ? err.stack : 'No stack');
      logger.warn('[PushNotif] Error subscribing:', { category: 'PWA', data: { err } });
      return false;
    }
  }, [isSupported, hotelId, getServiceWorkerRegistration]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await getServiceWorkerRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;

      await callEdgeFunction('save-push-subscription', 'DELETE', {
        endpoint: sub.endpoint,
      });

      await sub.unsubscribe();
      setStatus('idle');
      setIsSubscribed(false);
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      logger.warn('[PushNotif] Error unsubscribing:', { category: 'PWA', data: { err } });
    }
  }, [getServiceWorkerRegistration]);

  const sendNotification = useCallback(async (params: SendNotificationParams): Promise<SendResult> => {
    try {
      console.log('[SendNotification] Sending with params:', { hotel_id: hotelId, ...params });
      const result = await callEdgeFunction('send-push-notification', 'POST', {
        hotel_id: hotelId,
        ...params,
        sent_by: params.sent_by || 'admin',
      });
      console.log('[SendNotification] Result:', result);
      return result;
    } catch (err) {
      console.error('[SendNotification] Error:', err);
      return { success: false, error: String(err) };
    }
  }, [hotelId]);

  const loadSubscriberCount = useCallback(async () => {
    try {
      const result = await callEdgeFunction(
        'save-push-subscription',
        'GET',
        undefined,
        `hotel_id=${hotelId}`
      );
      setSubscriberCount(result.count ?? 0);
    } catch {
      setSubscriberCount(null);
    }
  }, [hotelId]);

  return {
    status,
    isSupported,
    isSubscribed,
    subscriberCount,
    requestPermission,
    unsubscribe,
    sendNotification,
    loadSubscriberCount,
  };
}
